<template>
    <div v-if="hasJobs" class="mr-3 uncomp-jobs">
        <v-tooltip text="Jobs Requiring Attention" location="bottom">
            <template v-slot:activator="{ props }">
                <v-badge @click="showJobs" :content="totalJobs">
                    <v-icon icon="mdi-file-document-alert-outline" v-bind="props" class="text-yellow-lighten-3"></v-icon>
                </v-badge>
            </template>
        </v-tooltip>
        <v-dialog v-model="jobModal"
                  persistent
                  :scrim="false">
            <v-card>
                <v-toolbar dark
                           color="primary">
                    <v-spacer></v-spacer>
                    <v-btn icon
                           dark
                           @click="jobModal = false">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-toolbar>
                <v-card-text>
                    <v-row>
                        <v-col cols="3">
                            <v-card color="indigo-accent-2" variant="flat" class="text-center cursor-pointer" @click="scrollToSection('uncompleted')">
                                <div>UNCOMPLETED</div>
                                <h2>{{ uncompletedJobs.length }}</h2>
                            </v-card>
                        </v-col>
                        <v-col cols="3">
                            <v-card color="cyan-darken-1" variant="flat" class="text-center cursor-pointer" @click="scrollToSection('uninvoiced')">
                                <div>UNINVOICED</div>
                                <h2>{{ uninvoicedJobs.length }}</h2>
                            </v-card>
                        </v-col>
                    </v-row>
                    <div v-if="uncompletedJobs && uncompletedJobs.length > 0" ref="uncompletedSection" class="mt-2 pt-2 border-t-sm">
                        <h4>Uncompleted Jobs</h4>
                        <p>Below is a list of jobs completed over 2 days ago that require completing and invoicing.</p>
                        <v-table>
                            <thead>
                                <tr>
                                    <th class="text-left">Job</th>
                                    <th class="text-left">Customer</th>
                                    <th class="text-left">Customer Number</th>
                                    <th class="text-left">Status</th>
                                    <th class="text-left">$</th>
                                    <th class="text-left">Date Completed</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="job in uncompletedJobs" :key="job.id">
                                    <td><router-link :to="'/'+$constants.entities.Job.ROUTE+job.id" @click="jobModal=false">{{ job.name }}</router-link></td>
                                    <td>{{ job.customerName }}</td>
                                    <td>{{ job.customerNumber }}</td>
                                    <td><type-chip :type="job.status" entity="Job" :object="job"></type-chip></td>
                                    <td>{{ numberWithCommas(job.jobSubTotal, 2) }}</td>
                                    <td><span v-if="job.dateCompleted">{{ $dayjs(job.dateCompleted).format("DD/MM/YYYY") }}</span></td>
                                </tr>
                            </tbody>
                        </v-table>
                    </div>
                    <div v-if="uninvoicedJobs && uninvoicedJobs.length > 0" ref="uninvoicedSection" class="mt-2 pt-2 border-t-sm">
                        <h4>Uninvoiced Jobs</h4>
                        <p>Below is a list of jobs that have not been correctly invoiced in Xero.</p>
                        <v-table>
                            <thead>
                                <tr>
                                    <th class="text-left">Job</th>
                                    <th class="text-left">Customer</th>
                                    <th class="text-left">Customer Number</th>
                                    <th class="text-left">Status</th>
                                    <th class="text-left">$</th>
                                    <th class="text-left">Date Completed</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="job in uninvoicedJobs" :key="job.id">
                                    <td> <router-link :to="'/'+$constants.entities.Job.ROUTE+job.id" @click="jobModal=false">{{ job.name }}</router-link></td>
                                    <td>{{ job.customerName }}</td>
                                    <td>{{ job.customerNumber }}</td>
                                    <td><type-chip :type="job.status" entity="Job" :object="job"></type-chip></td>
                                    <td>{{ numberWithCommas(job.jobSubTotal, 2) }}</td>
                                    <td><span v-if="job.dateCompleted">{{ $dayjs(job.dateCompleted).format("DD/MM/YYYY") }}</span></td>
                                </tr>
                            </tbody>
                        </v-table>
                    </div>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>
<style scoped>
    .uncomp-jobs {
        cursor:pointer;
    }
</style>
<script>
    import { numberWithCommas } from "../mixins/numberMixins";
    import { mapActions } from "vuex";
    function initialState() {
        return {
            saving: false,
            loading: false,
            uncompletedJobs: null,
            uninvoicedJobs: null,
            jobModal: false,
            refreshInterval: null
        };
    }
    export default {
        name: "UncompletedJobs",
        data: function () {
            return initialState();
        },
        mounted: function () {
            this.loadData();
            this.refreshInterval = setInterval(() => {
                this.loadData();
            }, 600000);
        },
        beforeUnmount: function () {
            // Clean up the interval when component is destroyed
            if (this.refreshInterval) {
                clearInterval(this.refreshInterval);
            }
        },
        props: {
            props: Object,
        },
        methods: {
            ...mapActions([
                "showErrorSnack",
                "showSuccessSnack"
            ]),
            numberWithCommas,
            showJobs() {
                this.jobModal = true;
            },
            scrollToSection(section) {
                const ref = section === 'uncompleted' ? this.$refs.uncompletedSection : this.$refs.uninvoicedSection;
                if (ref) {
                    ref.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            },
            reset: function () {
                Object.assign(this.$data, initialState());
            },
            loadData() {
                this.loading = true;
                this.$api.get('dashboard/jobsrequiringattention')
                    .then(res => {
                        this.uncompletedJobs = res.data.uncompleted;
                        this.uninvoicedJobs = res.data.uninvoiced;
                        this.loading = false;
                    }).catch(error => {
                        this.loading = false;
                        this.showErrorSnack("Whoops. We ran into an error.");
                    });
            }
        },
        computed: {
            totalJobs() {
                return (this.uncompletedJobs && this.uncompletedJobs.length > 0 ? this.uncompletedJobs.length : 0) || (this.uninvoicedJobs && this.uninvoicedJobs.length > 0 ? this.uninvoicedJobs.length : 0);
            },
            hasJobs() {
                return (this.uncompletedJobs && this.uncompletedJobs.length > 0) || (this.uninvoicedJobs && this.uninvoicedJobs.length > 0);
            }
        }
    };
</script>